import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Search, Globe, Share2, ShieldCheck, Sparkles } from "lucide-react";
import DropZone from "../components/DropZone";

const DEFAULT_SEO = {
  // Global identity
  site_name: "Places",
  site_url: "",
  default_title: "Places · The Real Estate Legacy",
  title_template: "%s | Places",
  default_description: "Agência de marketing imobiliário premium. Places transforma empreendimentos em legado com narrativa, dados e IA.",
  default_keywords: ["marketing imobiliário", "agência imobiliária", "lançamento imobiliário", "branding imobiliário", "Places"],
  organization_name: "Places",
  organization_logo: "",
  publisher: "Places",
  // Sharing defaults
  default_og_image: "",
  twitter_handle: "@agenciaplaces",
  twitter_card: "summary_large_image",
  // Structured data toggles
  enable_jsonld_website: true,
  enable_jsonld_org: true,
  // Robots / indexing
  default_robots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  robots_txt: "",
  // Verifications
  google_site_verification: "",
  bing_site_verification: "",
  facebook_domain_verification: "",
  pinterest_site_verification: "",
  yandex_verification: "",
  // Social profiles (for JSON-LD sameAs)
  social_urls: [],
};

export default function SEOEditor() {
  const [seo, setSeo] = useState(DEFAULT_SEO);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/kv/seo").then(({ data }) => data && setSeo({ ...DEFAULT_SEO, ...data }));
  }, []);

  const upd = (k, v) => setSeo((s) => ({ ...s, [k]: v }));
  const updArr = (k, v) => upd(k, v.split(",").map((s) => s.trim()).filter(Boolean));
  const updArrN = (k, v) => upd(k, v.split("\n").map((s) => s.trim()).filter(Boolean));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/kv/seo", { value: seo });
      toast.success("SEO global salvo.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl">SEO Global</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Metadados aplicados como padrão em todas as páginas. Cases e Insights podem sobrescrever campo a campo.
          </p>
        </div>
        <button onClick={save} disabled={saving} data-testid="seo-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar SEO"}</button>
      </div>

      {/* Identidade / Base */}
      <section className="border border-black/10 p-5 rounded mb-6">
        <div className="flex items-center gap-2 mb-4"><Globe className="w-4 h-4" /><div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Identidade & Base</div></div>
        <div className="grid md:grid-cols-2 gap-3">
          <F label="Site name (marca)" v={seo.site_name} on={(v) => upd("site_name", v)} testid="seo-site-name" />
          <F label="Site URL canônica (ex: https://agenciaplaces.com.br)" v={seo.site_url} on={(v) => upd("site_url", v)} testid="seo-site-url" />
          <F label="Título padrão (Home)" v={seo.default_title} on={(v) => upd("default_title", v)} testid="seo-default-title" />
          <F label="Template de título (%s = título da página)" v={seo.title_template} on={(v) => upd("title_template", v)} testid="seo-title-template" />
          <T label="Meta description padrão (até ~160 caracteres)" v={seo.default_description} on={(v) => upd("default_description", v)} rows={3} testid="seo-default-desc" />
          <F label="Publisher (nome legal)" v={seo.publisher} on={(v) => upd("publisher", v)} />
          <F label="Organization Name (JSON-LD)" v={seo.organization_name} on={(v) => upd("organization_name", v)} />
          <F label="Organization Logo URL (quadrado, min 112x112)" v={seo.organization_logo} on={(v) => upd("organization_logo", v)} />
        </div>
        <div className="mt-3">
          <label className="text-xs text-neutral-500">Palavras-chave padrão (vírgulas)</label>
          <input value={(seo.default_keywords || []).join(", ")} onChange={(e) => updArr("default_keywords", e.target.value)} data-testid="seo-default-kw" className="w-full border border-black/15 px-3 py-2 rounded text-sm" placeholder="marketing imobiliário, agência, lançamento" />
          <div className="text-[10px] text-neutral-400 mt-1">{(seo.default_keywords || []).length} palavras. São mescladas com as keywords específicas de cada página.</div>
        </div>
      </section>

      {/* Social / Sharing */}
      <section className="border border-black/10 p-5 rounded mb-6">
        <div className="flex items-center gap-2 mb-4"><Share2 className="w-4 h-4" /><div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Compartilhamento (Open Graph & Twitter)</div></div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-neutral-500">OG image padrão (1200×630)</label>
            {seo.default_og_image && <img src={seo.default_og_image} alt="" className="max-h-24 my-2 border border-black/10 rounded" />}
            <DropZone multiple={false} onUploaded={(u) => upd("default_og_image", u.url)} />
            <input value={seo.default_og_image || ""} onChange={(e) => upd("default_og_image", e.target.value)} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-2" data-testid="seo-og-image" />
          </div>
          <div className="space-y-3">
            <F label="Twitter handle (@marca)" v={seo.twitter_handle} on={(v) => upd("twitter_handle", v)} />
            <div>
              <label className="text-xs text-neutral-500">Twitter Card type</label>
              <select value={seo.twitter_card} onChange={(e) => upd("twitter_card", e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm bg-white">
                <option value="summary_large_image">summary_large_image (recomendado)</option>
                <option value="summary">summary</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-neutral-500">URLs de redes sociais (sameAs — 1 por linha)</label>
              <textarea value={(seo.social_urls || []).join("\n")} onChange={(e) => updArrN("social_urls", e.target.value)} rows={4} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" placeholder="https://instagram.com/agenciaplaces&#10;https://linkedin.com/company/agenciaplaces" data-testid="seo-social-urls" />
            </div>
          </div>
        </div>
      </section>

      {/* Structured data */}
      <section className="border border-black/10 p-5 rounded mb-6">
        <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4" /><div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Structured Data (JSON-LD)</div></div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm border border-black/10 px-3 py-2 rounded">
            <input type="checkbox" checked={!!seo.enable_jsonld_website} onChange={(e) => upd("enable_jsonld_website", e.target.checked)} data-testid="seo-jsonld-website" /> Emitir schema WebSite + SearchAction
          </label>
          <label className="flex items-center gap-2 text-sm border border-black/10 px-3 py-2 rounded">
            <input type="checkbox" checked={!!seo.enable_jsonld_org} onChange={(e) => upd("enable_jsonld_org", e.target.checked)} data-testid="seo-jsonld-org" /> Emitir schema Organization
          </label>
        </div>
        <div className="text-[11px] text-neutral-500 mt-2">Article JSON-LD é gerado automaticamente em páginas de Insights/Cases (og:type = article).</div>
      </section>

      {/* Robots / Indexing */}
      <section className="border border-black/10 p-5 rounded mb-6">
        <div className="flex items-center gap-2 mb-4"><Search className="w-4 h-4" /><div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Indexação & Robots</div></div>
        <F label="Robots meta padrão" v={seo.default_robots} on={(v) => upd("default_robots", v)} />
        <div className="mt-3">
          <label className="text-xs text-neutral-500">robots.txt customizado (deixe em branco para usar o gerado automaticamente)</label>
          <textarea value={seo.robots_txt || ""} onChange={(e) => upd("robots_txt", e.target.value)} rows={6} className="w-full border border-black/15 px-3 py-2 rounded text-xs font-mono" placeholder="User-agent: *&#10;Allow: /&#10;Sitemap: https://agenciaplaces.com.br/sitemap.xml" data-testid="seo-robots-txt" />
          <div className="text-[10px] text-neutral-400 mt-1">Servido em <code>/robots.txt</code>. Sitemap em <code>/sitemap.xml</code> (auto).</div>
        </div>
      </section>

      {/* Verifications */}
      <section className="border border-black/10 p-5 rounded mb-6">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-4 h-4" /><div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Verificações de propriedade</div></div>
        <div className="grid md:grid-cols-2 gap-3">
          <F label="Google Site Verification" v={seo.google_site_verification} on={(v) => upd("google_site_verification", v)} testid="seo-google-verify" />
          <F label="Bing (msvalidate.01)" v={seo.bing_site_verification} on={(v) => upd("bing_site_verification", v)} />
          <F label="Facebook Domain Verification" v={seo.facebook_domain_verification} on={(v) => upd("facebook_domain_verification", v)} />
          <F label="Pinterest (p:domain_verify)" v={seo.pinterest_site_verification} on={(v) => upd("pinterest_site_verification", v)} />
          <F label="Yandex Verification" v={seo.yandex_verification} on={(v) => upd("yandex_verification", v)} />
        </div>
      </section>
    </div>
  );
}

function F({ label, v, on, testid }) {
  return (<div><label className="text-xs text-neutral-500">{label}</label><input value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testid} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>);
}
function T({ label, v, on, rows = 3, testid }) {
  return (<div className="md:col-span-2"><label className="text-xs text-neutral-500">{label}</label><textarea value={v ?? ""} onChange={(e) => on(e.target.value)} rows={rows} data-testid={testid} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>);
}
